/**
 * @file desktop error boundary component
 */

import { faPageBreak } from '@fortawesome/pro-duotone-svg-icons'
import { Icon, Modal } from '@seiue/ui'
import { isWebpack4ChunkLoadError } from '@seiue/util'
import React from 'react'
import { ErrorBoundary as ErrorBoundaryRaw } from 'react-error-boundary'
import { useLocation } from 'react-router'
import styled from 'styled-components'

interface FallbackProps {
  error: Error
  resetErrorBoundary: () => void
}

const MODAL_COOL_DOWN_TIME = 3000

const ErrorFallback: React.FC<FallbackProps> = ({ error }) => {
  // 因为网络不稳定等原因, 下载 js/css 资源时可能会失败
  const message = isWebpack4ChunkLoadError(error)
    ? '网络状况不佳，可尝试刷新页面'
    : '错误已自动上报，我们将尽快处理'

  return (
    <ErrorHintContainer>
      <Icon
        icon={faPageBreak}
        style={{ fontSize: '48px' }}
      />
      <ErrorTitle>{'页面加载失败了'}</ErrorTitle>
      <ErrorSubtitle>{message}</ErrorSubtitle>
    </ErrorHintContainer>
  )
}

/**
 * ErrorBoundary
 *
 * @param props - 参数
 * @param props.children - children
 * @returns ErrorBoundary
 */
export const ErrorBoundary: React.FC = (props) => {
  const location = useLocation()
  // 创建一个变量来记录最后一次弹出时间
  const lastModalTimeRef = React.useRef(0)

  return (
    <ErrorBoundaryRaw
      FallbackComponent={ErrorFallback}
      resetKeys={[location]}
      onError={(e) => {
        const now = Date.now()
        // 三秒内只弹出一次
        if (now - lastModalTimeRef.current > MODAL_COOL_DOWN_TIME) {
          lastModalTimeRef.current = now
          Modal.warning({
            title: '页面加载失败了',
            content: isWebpack4ChunkLoadError(e)
              ? '网络状况不佳，可尝试刷新页面'
              : '错误已自动上报，我们将尽快处理',
          })
        }
      }}
    >
      {props.children}
    </ErrorBoundaryRaw>
  )
}

const ErrorTitle = styled.div`
  margin: 30px 0 10px 0;
  color: #323842;
  font-weight: bold;
  font-size: 18px;
`

const ErrorSubtitle = styled.div`
  color: rgba(6, 12, 25, 0.38);
  font-size: 14px;
`

const ErrorHintContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;

  /* 调整视觉重心 */
  margin-top: -5%;
`
